import {
	type FilterField,
	useFilterBuilder,
	useUrlPagination,
} from "@monbits/ui";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";
import { Outlet, useSearchParams } from "react-router-dom";
import auditQueries from "src/queries/audit-queries";
import type { ActionCategory, AuditLog } from "src/types";
import { AUDIT_CATEGORY_CONFIG } from "@/utils/audit-log";
import { AuditLogsList } from "./audit-logs-list";

export type AuditLogsOutletContext = {
	logs: AuditLog[];
	isLoading: boolean;
};

// Generate filter options from the config
const CATEGORY_OPTIONS = Object.entries(AUDIT_CATEGORY_CONFIG).map(
	([value, config]) => ({
		value,
		label: config.label,
		icon: (
			<div
				className="h-6 w-6 rounded flex items-center justify-center"
				style={{
					backgroundColor: `${config.color}20`,
					color: config.color,
				}}
			>
				<config.icon className="h-3.5 w-3.5" />
			</div>
		),
	}),
);

export const AUDIT_FILTER_FIELDS: FilterField[] = [
	{
		id: "actionCategory",
		label: "Category",
		type: "select",
		options: CATEGORY_OPTIONS,
	},
];

export function AuditLogsLayout() {
	const [searchParams, setSearchParams] = useSearchParams();

	const { filters, setFilters } = useFilterBuilder({
		fields: AUDIT_FILTER_FIELDS,
		searchParams,
		setSearchParams,
	});

	const search = searchParams.get("search") || "";

	// Convert filter builder filters to API params
	const apiFilters = React.useMemo(() => {
		const result: {
			actorEmail?: string;
			actionCategory?: ActionCategory;
		} = {};

		// Add search as actorEmail filter
		if (search) {
			result.actorEmail = search;
		}

		for (const f of filters) {
			if (f.operator === "is" && f.field === "actionCategory") {
				result.actionCategory = f.value as ActionCategory;
			}
		}

		return result;
	}, [filters, search]);

	const { data } = useQuery(
		auditQueries.listAuditLogs({
			limit: 20,
			offset: 0,
		}),
	);
	const totalCount = data?.totalCount ?? 0;

	const { page, limit, offset, setPage, setLimit } = useUrlPagination({
		totalCount,
	});

	const listParams = React.useMemo(
		() => ({
			limit,
			offset,
			...apiFilters,
		}),
		[limit, offset, apiFilters],
	);

	const { data: logsData, isLoading: logsLoading } = useQuery(
		auditQueries.listAuditLogs(listParams),
	);
	const logs = logsData?.logs ?? [];
	const logsTotal = logsData?.totalCount ?? 0;

	return (
		<>
			<AuditLogsList
				filters={filters}
				setFilters={setFilters}
				logs={logs}
				totalCount={logsTotal}
				isLoading={logsLoading}
				page={page}
				limit={limit}
				onPageChange={setPage}
				onPageSizeChange={setLimit}
			/>
			<Outlet context={{ logs, isLoading: logsLoading }} />
		</>
	);
}
